import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { useMedplum } from "@medplum/react";

import { Button, Drawer, Group, NumberInput, rem, Stack, Textarea } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm, zodResolver } from "@mantine/form";

import { IconCalendar } from "@tabler/icons-react";
import { parseISO } from "date-fns";
import { v4 as uuidv4 } from "uuid";

import { closeOutPregnancy } from "@/clients/services/closeOut";
import {
  pregnancyCloseoutFormSchema,
  PregnancyCloseoutFormValues,
} from "@/clients/zod-schemas/pregnancy-closeout";
import ValueSetSearchInput from "@/components/ValueSetSearchInput";
import { LOINCValueSets } from "@/fhir/enums/loinc";
import {
  calculateGestationalAgeInWeeksAndDays,
  createPostpartumTracker,
  dispatchPostpartumForDeliveryDate,
  fetchAndDispatchPregnancyRecords,
} from "@/pregnancy";
import { useAppDispatch } from "@/store";
import { selectCurrentPregnancyRecord, setList } from "@/store/slices/pregnancy";
import { showSystemErrorNotification, toast } from "@/utils/notifications";

interface CloseoutDrawerProps {
  opened: boolean;
  onClose: () => void;
  trigger: () => void;
}

export default function CloseoutDrawer({ opened, onClose, trigger }: CloseoutDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useAppDispatch();
  const medplum = useMedplum();
  const { clientId } = useParams() as { clientId: string };

  const activePregnancyRecord = useSelector(selectCurrentPregnancyRecord(clientId));

  const isGestationalAgeManuallyEditedRef = useRef(false);

  const form = useForm<PregnancyCloseoutFormValues>({
    initialValues: {
      closeoutDate: new Date(),
      outcome: {
        code: "",
        system: "",
        display: "",
      },
      gestationalAge: undefined,
      notes: "",
    },
    validate: zodResolver(pregnancyCloseoutFormSchema),
  });

  /** Pre-fills GA from EDD + close-out date unless the user has manually edited GA. */
  const syncGestationalAgeFromEdd = (
    closeoutDate: Date | null | undefined,
    edd: string | undefined,
  ) => {
    if (!closeoutDate || !edd || isGestationalAgeManuallyEditedRef.current) return;
    const { weeks } = calculateGestationalAgeInWeeksAndDays(edd, closeoutDate);
    form.setFieldValue("gestationalAge", weeks >= 0 ? weeks : undefined);
  };

  useEffect(() => {
    if (!opened) return;
    isGestationalAgeManuallyEditedRef.current = false;
    syncGestationalAgeFromEdd(form.values.closeoutDate, activePregnancyRecord?.edd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, activePregnancyRecord?.edd]);

  const submitCloseout = async (values: PregnancyCloseoutFormValues) => {
    setIsSubmitting(true);
    // Capture list ID before closeout — the active record becomes retired after closeOutPregnancy.
    const pregnancyRecordId = activePregnancyRecord?.list.id;
    try {
      const closeoutIdentifier = uuidv4();
      await closeOutPregnancy(medplum, clientId, values, closeoutIdentifier);
      // Refresh state.pregnancy so the UI immediately reflects isActive: false on the
      // retired record (closeOutPregnancy → closePregnancyRecord sets List.status to "retired").
      await fetchAndDispatchPregnancyRecords(medplum, dispatch, clientId);
      toast({
        title: "Pregnancy closed",
        message: "Pregnancy closed successfully.",
        type: "success",
        autoClose: 3000,
      });
      onClose();
      const { updatedPregnancyList } = await createPostpartumTracker(
        medplum,
        clientId,
        values.closeoutDate,
        closeoutIdentifier,
        pregnancyRecordId,
      );
      if (updatedPregnancyList) {
        dispatch(setList({ patientId: clientId, list: updatedPregnancyList }));
      }
      if (pregnancyRecordId) {
        dispatchPostpartumForDeliveryDate(
          dispatch,
          clientId,
          pregnancyRecordId,
          values.closeoutDate,
        );
      }
      trigger();
    } catch (error) {
      showSystemErrorNotification();
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    return form.onSubmit((values) => submitCloseout(values))(e);
  };

  const handleCloseoutDateChange = (value: string | null) => {
    if (!value) return;
    const closeoutDate = parseISO(value);
    form.getInputProps("closeoutDate").onChange(closeoutDate);
    isGestationalAgeManuallyEditedRef.current = false;
    syncGestationalAgeFromEdd(closeoutDate, activePregnancyRecord?.edd);
  };

  const handleGestationalAgeChange = (value: string | number) => {
    isGestationalAgeManuallyEditedRef.current = true;
    form.getInputProps("gestationalAge").onChange(value);
  };

  return (
    <Drawer.Root size="xl" opened={opened} position="right" onClose={onClose}>
      <Drawer.Overlay />
      <Drawer.Content data-testid="pregnancy-closeout-drawer">
        <form onSubmit={handleSubmit}>
          <Drawer.Header px="md" h={rem(64)} bg="gray.0">
            <Group w="100%" justify="space-between">
              <Group gap="xs">
                <Drawer.CloseButton />
                <Drawer.Title>Confirm Close Out</Drawer.Title>
              </Group>
              <Button
                type="submit"
                color="burgundy.9"
                loading={isSubmitting}
                data-testid="pregnancy-closeout-confirm-button"
              >
                Confirm
              </Button>
            </Group>
          </Drawer.Header>
          <Drawer.Body p="lg">
            <Stack gap="lg">
              <DateInput
                maxDate={new Date()}
                placeholder="MM/DD/YYYY"
                valueFormat="MM/DD/YYYY"
                label="Pregnancy Close Out Date"
                leftSection={<IconCalendar size={16} />}
                data-testid="pregnancy-closeout-date-input"
                {...form.getInputProps("closeoutDate")}
                onChange={handleCloseoutDateChange}
              />
              <ValueSetSearchInput
                preload
                required
                label="Outcome"
                binding={LOINCValueSets.PregnancyOutcome}
                data-testid="pregnancy-closeout-outcome-input"
                {...form.getInputProps("outcome")}
              />
              <NumberInput
                min={0}
                required
                hideControls
                suffix=" weeks"
                label="Gestational Age (GA)"
                placeholder="Weeks pregnant"
                data-testid="pregnancy-closeout-gestational-age-input"
                {...form.getInputProps("gestationalAge")}
                onChange={handleGestationalAgeChange}
              />
              <Textarea
                autosize
                minRows={3}
                placeholder="Enter notes"
                data-testid="pregnancy-closeout-notes"
                label="Are there any additional facts about this pregnancy we should know?"
                {...form.getInputProps("notes")}
              />
            </Stack>
          </Drawer.Body>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
}
